/**
 * Action Handler Registry — maps action types to handler functions.
 *
 * Extracted from useWorkspaceActions.js to make action handling extensible.
 * Built-in action types are pre-registered. Custom action types can be added
 * by plugins or workspace extensions.
 *
 * Usage:
 *   // Register a custom action handler
 *   actionHandlerRegistry.register('INTEGRATION_SEND', (action, row, ctx) => {
 *     IntegrationService.sendOutbound(action.channelCode, row);
 *   });
 *
 *   // Resolve and execute in useWorkspaceActions
 *   const handler = actionHandlerRegistry.resolve(actionType);
 *   if (handler) handler(action, rowData, context);
 *
 * Handler signature:
 *   (action, rowData, context) => void
 *
 *   action  — the action definition from entity_action_metadata
 *   rowData — the grid row data (null for global actions)
 *   context — workspace context object with helpers:
 *     { setActiveViewId, openForm, executeAction, loadMasterData,
 *       setOverlayMode, setOverlayEntity, setOverlayRowData, setOverlayAction,
 *       setPickerConfig, setPickerOpen, setPickerParentRow,
 *       setCriteriaBuilderConfig, setCriteriaBuilderOpen, setCriteriaBuilderRow,
 *       setCountEntryConfig, setCountEntryOpen, setCountEntryRow,
 *       setBulkConfirmConfig, setBulkConfirmOpen, setBulkConfirmRows, setBulkConfirmScript,
 *       setConfirmAction, setConfirmRow, setConfirmOpen,
 *       activeView, resolvedDetails, pendingNavParamsRef, onError, FlexService }
 */

const registry = new Map();

const actionHandlerRegistry = {
  /**
   * Register an action handler.
   * @param {string} actionType - Action type (e.g., 'NAVIGATE', 'WIZARD', 'INTEGRATION_SEND')
   * @param {Function} handler - (action, rowData, context) => void
   */
  register(actionType, handler) {
    if (!actionType) throw new Error('Action type is required');
    if (typeof handler !== 'function') throw new Error('Handler must be a function for: ' + actionType);
    registry.set(actionType, handler);
  },

  /**
   * Resolve a handler by action type.
   * @param {string} actionType
   * @returns {Function | null}
   */
  resolve(actionType) {
    return registry.get(actionType) || null;
  },

  /**
   * Check if a handler is registered.
   * @param {string} actionType
   * @returns {boolean}
   */
  has(actionType) {
    return registry.has(actionType);
  },

  /**
   * List all registered action types.
   * @returns {string[]}
   */
  list() {
    return Array.from(registry.keys());
  },

  /**
   * Register multiple handlers at once.
   * @param {Object<string, Function>} handlers
   */
  registerAll(handlers) {
    for (const [type, handler] of Object.entries(handlers)) {
      this.register(type, handler);
    }
  },
};

// ========== Built-in Action Handlers ==========
// These are extracted from useWorkspaceActions.js and pre-registered.
// Each handler receives (action, rowData, ctx) where ctx has all workspace helpers.

actionHandlerRegistry.register('NAVIGATE', (action, rowData, ctx) => {
  // Resolve contextParams once -- used either as this-workspace nav params
  // (pendingNavParamsRef, existing behavior) or, for a cross-workspace jump,
  // as the opened tab's initial filter (navContext.filters), same source
  // data either way.
  let params = null;
  if (action.contextParams && rowData) {
    params = {};
    for (const [paramName, rowField] of Object.entries(action.contextParams)) {
      // A single-quoted value ('ITEM') is a literal constant, not a row-field
      // lookup -- lets a NAVIGATE drill into a generic entity (e.g. EntityAttribute)
      // pre-scoped to a fixed entityId alongside a dynamic row field (e.g. itemId
      // as configKey). No existing contextParams value was ever quoted, so this
      // is backward compatible with every prior caller.
      if (typeof rowField === 'string' && rowField.length >= 2 && rowField.startsWith("'") && rowField.endsWith("'")) {
        params[paramName] = rowField.slice(1, -1);
      } else {
        params[paramName] = rowData[rowField];
      }
    }
  }

  if (action.targetWorkspace) {
    // Cross-workspace NAVIGATE -- opens a DIFFERENT workspace as a tab
    // (focuses an existing one of the same type rather than duplicating,
    // same as every other openWorkspace caller), landing on targetView
    // with the row's context params applied as an advanced-search filter.
    // See MetadataWorkspace's navContext effect for how viewId/filters are
    // consumed on the receiving end.
    if (!ctx.onOpenWorkspace) return;
    ctx.onOpenWorkspace(action.targetWorkspace, action.workspaceTitle, false, {
      viewId: action.targetView,
      filters: params,
    });
    return;
  }

  if (action.targetView) {
    if (params) {
      ctx.pendingNavParamsRef.current = params;
    }
    ctx.setActiveViewId(action.targetView);
  }
});

actionHandlerRegistry.register('PDF_VIEW', (action, rowData, ctx) => {
  if (!action.pdfUrl) return;
  const url = action.pdfUrl.replace(/\{\{(\w+)\}\}/g, (_, key) => rowData?.[key] ?? '');
  const token = localStorage.getItem('waide_access_token');
  fetch(`/api/v1/proxy/wms${url}`, { headers: { 'Authorization': `Bearer ${token}` } })
    .then(async (resp) => {
      if (!resp.ok) {
        // Say why, not just that it failed: a 403 is a missing permission, anything else
        // carries the proxy's own message ("Report generation failed: ...").
        if (resp.status === 403) throw new Error("You don't have permission to print this report.");
        const body = await resp.text().catch(() => '');
        let detail = body;
        try { detail = JSON.parse(body).message || body; } catch (e) { /* plain text body */ }
        throw new Error(`Failed to generate report${detail ? `: ${String(detail).slice(0, 200)}` : ''}`);
      }
      return resp.blob();
    })
    .then(blob => {
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
    })
    .catch(err => {
      console.error('PDF view failed:', err);
      if (ctx.onError) ctx.onError(err.message);
    });
});

// Opens an arbitrary URL in a new tab, with the same {{field}} row-templating
// PDF_VIEW uses. Built for FAST_CONFIG's "Scripts" (FLEX Commands) tab so
// Edit opens the real FAST Studio Flex IDE with that command pre-loaded
// (?workspace=flex&scriptId={{id}}) instead of a generic metadata form --
// see project_flex_commands_rename_and_studio_edit_link. Generic enough for
// any other "open this row somewhere else" action too.
actionHandlerRegistry.register('OPEN_URL', (action, rowData, ctx) => {
  if (!action.url) return;
  const url = action.url.replace(/\{\{(\w+)\}\}/g, (_, key) => rowData?.[key] ?? '');
  window.open(url, action.target || '_blank');
});

actionHandlerRegistry.register('EDIT', (action, rowData, ctx) => {
  ctx.setOverlayMode('edit');
  ctx.setOverlayEntity(ctx.activeView?.master?.entity);
  ctx.setOverlayRowData(rowData);
  ctx.setOverlayAction(action);
});

actionHandlerRegistry.register('VIEW', (action, rowData, ctx) => {
  ctx.setOverlayMode('view');
  ctx.setOverlayEntity(action.formEntity || ctx.activeView?.master?.entity);
  ctx.setOverlayRowData(rowData);
  ctx.setOverlayAction(action);
});

actionHandlerRegistry.register('FORM', (action, rowData, ctx) => {
  if (action.rapidEntry) {
    // Rapid-entry receiving (resolver phase + scan loop). Row action provides rowData,
    // global action does not — in which case the resolver phase runs first.
    ctx.openRapidEntry({ actionLabel: action.label, ...action.rapidEntry }, rowData);
    return;
  }
  _handleFormWizard(action, rowData, ctx);
});

actionHandlerRegistry.register('WIZARD', (action, rowData, ctx) => {
  if (action.rapidEntry) {
    ctx.openRapidEntry({ actionLabel: action.label, ...action.rapidEntry }, rowData);
    return;
  }
  _handleFormWizard(action, rowData, ctx);
});

function _handleFormWizard(action, rowData, ctx) {
  const fEntity = action.formEntity || ctx.activeView?.master?.entity;
  const fDetail = action.formDetailEntity || (action.formEntity ? null : ctx.resolvedDetails?.[0]?.entity) || null;
  // the submit button says what it does: "Edit X" saves, "New X"/"Add X" creates; other verbs (Receive...) stay as labelled
  const saveLabel = /^(edit|update|modify|change)\b/i.test(action.label || '') ? 'Save'
    : /^(new|create|add)\b/i.test(action.label || '') ? 'Create'
    : action.label;
  const formOpts = { scriptName: action.scriptName, title: action.label, saveLabel, forceCreate: !!action.forceCreate };

  // "prefill" maps target-field-name -> source-row-field-name, same convention
  // as NAVIGATE's contextParams -- used to open a CREATE form for a *different*
  // entity than the row's own, seeded with just the named field(s) (e.g. "Add
  // Location" from an Area row, prefilling Location.areaId from the Area's own
  // areaId). Deliberately builds a minimal object rather than passing the
  // whole row through, so unrelated fields from the source entity never leak
  // into the new record's save payload.
  if (action.prefill && rowData) {
    const prefillData = {};
    Object.entries(action.prefill).forEach(([targetField, sourceField]) => {
      prefillData[targetField] = rowData[sourceField];
    });
    ctx.openForm(fEntity, fDetail, action.formKey || action.wizardKey, prefillData, formOpts);
    return;
  }

  if (action.loadScript && rowData) {
    ctx.FlexService.executeScript({ scriptName: action.loadScript, context: { _payload: rowData } })
      .then(resp => {
        const loaded = resp?.context?.result || {};
        const merged = { ...rowData, ...loaded };
        ctx.openForm(fEntity, fDetail, action.formKey || action.wizardKey, merged, formOpts);
      })
      .catch(() => {
        ctx.openForm(fEntity, fDetail, action.formKey || action.wizardKey, rowData, formOpts);
      });
  } else if (!rowData && ctx.navParams && Object.keys(ctx.navParams).length > 0) {
    // A header CREATE action (no rowData, no explicit prefill/loadScript) has
    // no other way to know which parent it's scoped to when opened from a
    // NAVIGATE-drilled-into view -- e.g. Zones' "Add Location" on the
    // zone-members view needs zoneId, which only exists as navParams (set by
    // the NAVIGATE action's own contextParams, see the 'NAVIGATE' handler
    // above). forceCreate:true keeps this a real create (not "edit") despite
    // seedData being non-null -- see EntityWizardOverlay's forceCreate prop.
    ctx.openForm(fEntity, fDetail, action.formKey || action.wizardKey, { ...ctx.navParams }, { ...formOpts, forceCreate: true });
  } else {
    ctx.openForm(fEntity, fDetail, action.formKey || action.wizardKey, rowData, formOpts);
  }
}

actionHandlerRegistry.register('BULK_CONFIRM', (action, rowData, ctx) => {
  if (!action.confirmGrid) return;
  ctx.setBulkConfirmRows(rowData?.selected_rows || []);
  ctx.setBulkConfirmConfig({ ...action.confirmGrid, title: action.label, confirmLabel: action.label });
  ctx.setBulkConfirmScript(action.scriptName);
  ctx.setBulkConfirmOpen(true);
});

// Add a custom field ("mode": "add"), or change the row's one ("mode": "edit"), with the same
// dialog people use from an entity's form (FH-205)
actionHandlerRegistry.register('CUSTOM_FIELD', (action, rowData, ctx) => {
  ctx.setCustomFieldDialog(action.mode === 'edit' && rowData ? { field: rowData } : {});
});

// The photos on the row's record (taken on the RF gun or added here):
// "photos": { "entity": "Receipt", "idField": "receiptId", "noun": "receipt" }
actionHandlerRegistry.register('PHOTOS', (action, rowData, ctx) => {
  const cfg = action.photos;
  const entityId = cfg && rowData?.[cfg.idField];
  if (!entityId) return;
  ctx.setPhotosDialog({ entity: cfg.entity, entityId: String(entityId), label: `${cfg.noun || cfg.entity.toLowerCase()} ${entityId}` });
});

// Start an inspection or a work order on the row, or open a run, in the stepped panel (FH-364/366):
// "wizardRun": { "startScript": "inspection_start", "payload": { "lpn": "associatedLpn" } } starts one;
// "wizardRun": { "runField": "runNumber" } opens the row's run where it stands.
// "values" adds fixed values to the start payload, e.g. which workflow a warehouse's own Start button starts (FH-371).
actionHandlerRegistry.register('WIZARD_RUN', (action, rowData, ctx) => {
  const cfg = action.wizardRun;
  if (!cfg) return;
  if (cfg.runField) {
    const runRef = rowData?.[cfg.runField];
    if (runRef) ctx.setWizardRunDialog({ run: String(runRef) });
    return;
  }
  const payload = { ...(cfg.values || {}) };
  Object.entries(cfg.payload || {}).forEach(([key, field]) => { payload[key] = rowData?.[field]; });
  ctx.setWizardRunDialog({ startScript: cfg.startScript || action.scriptName, payload });
});

actionHandlerRegistry.register('PICKER', (action, rowData, ctx) => {
  if (!action.pickerConfig) return;
  ctx.setPickerConfig(action.pickerConfig);
  ctx.setPickerParentRow(rowData);
  ctx.setPickerOpen(true);
});

actionHandlerRegistry.register('CRITERIA_BUILDER', (action, rowData, ctx) => {
  if (!action.criteriaBuilder) return;
  ctx.setCriteriaBuilderConfig(action.criteriaBuilder);
  ctx.setCriteriaBuilderRow(rowData);
  ctx.setCriteriaBuilderOpen(true);
});

actionHandlerRegistry.register('COUNT_ENTRY', (action, rowData, ctx) => {
  if (!action.countEntry) return;
  ctx.setCountEntryConfig(action.countEntry);
  ctx.setCountEntryRow(rowData);
  ctx.setCountEntryOpen(true);
});

actionHandlerRegistry.register('REFRESH', (action, rowData, ctx) => {
  ctx.loadMasterData();
});

// FH-419: hands a sentence to Atom (the chat opens and sends it), with {field} filled from the row --
// e.g. Learning > Start sends "start exercise <key>", which Atom answers with the exercise's first card.
actionHandlerRegistry.register('ASK_ATOM', (action, rowData) => {
  const template = action.atom?.sentence;
  if (!template) return;
  const sentence = template.replace(/\{([A-Za-z0-9_]+)\}/g, (all, field) => (rowData?.[field] ?? all));
  window.dispatchEvent(new CustomEvent('atom:ask', { detail: { sentence } }));
});

export default actionHandlerRegistry;
